// Additional imports for charts
import Grid from '@mui/material/Grid';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  CardHeader,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Tooltip,
  TextField
} from '@mui/material';
import Rating from '@mui/material/Rating';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  LineChart as ReLineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as ReTooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import ScienceIcon from '@mui/icons-material/Science';
import ChatHistoryModal from './ChatHistoryModal';
import ChatRatingModal from './ChatRatingModal';
import UsageLimitDisplay from '../common/UsageLimitDisplay';
import Cookies from '../../common/Cookies'; // Add this import
import { formatDateForDisplay } from '../../utils/timezoneUtils';
import Skeleton from '@mui/material/Skeleton';

// --- TopAgentsCard component copied from Dashboard.js ---
const TopAgentsCard = ({ agentData, loading, error }) => {
  const formatNumber = (value) => new Intl.NumberFormat('en-US').format(value);
  return (
    <Card sx={{ height: 180, display: 'flex', flexDirection: 'column', minWidth: 260 }}>
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mb: 1 }}>
          Top 5 Agents by Chats
        </Typography>
        {loading ? (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} variant="text" height={20} sx={{ mb: 1 }} />
            ))}
          </Box>
        ) : error ? (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              This data is not available right now, please try again later.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ flex: 1, overflow: 'hidden' }}>
            {agentData && agentData.length > 0 ? (
              <ul style={{ paddingLeft: 0, margin: 0, listStyle: 'none' }}>
                {agentData.slice(0, 5).map((agent, index) => (
                  <li key={agent.agentId || index} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontWeight: index === 0 ? 'bold' : 'normal', maxWidth: '60%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {index + 1}. {agent.agentName || 'Unknown Agent'}
                    </span>
                    <span style={{ fontWeight: 'bold', color: '#1976d2' }}>
                      {formatNumber(agent.sessionCount || 0)}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No agent data available
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

// ...existing code...
const TenantDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  // Debug: log dashboardData and metricsOverTime before rendering charts
  useEffect(() => {
    console.log('DEBUG: dashboardData', dashboardData);
    if (dashboardData && dashboardData.metricsOverTime) {
      console.log('DEBUG: metricsOverTime', dashboardData.metricsOverTime);
    } else {
      console.log('DEBUG: metricsOverTime is missing or empty');
    }
  }, [dashboardData]);
  // Billing summary state (MTD, all agents)
  const [billingSummary, setBillingSummary] = useState({ chats: 0, messages: 0, storage: '0 bytes', loading: true, error: null });

  // Fetch billing summary (MTD, all agents) on mount
  useEffect(() => {
    const fetchBillingSummary = async () => {
      setBillingSummary(prev => ({ ...prev, loading: true, error: null }));
      try {
        const today = new Date();
        const startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        const endDate = new Date(today);
        const formattedStartDate = startDate.toISOString().split('T')[0];
        const formattedEndDate = endDate.toISOString().split('T')[0];
        const url = `/dashboard/getDashboardData?startDate=${formattedStartDate}&endDate=${formattedEndDate}`;
        const response = await axios.get(url);
        setBillingSummary({
          chats: response.data?.summary?.totalSessions || 0,
          messages: response.data?.summary?.totalMessages || 0,
          storage: response.data?.summary?.storageHuman || '0 bytes',
          loading: false,
          error: null
        });
      } catch (err) {
        setBillingSummary(prev => ({ ...prev, loading: false, error: 'Could not load billing summary' }));
      }
    };
    fetchBillingSummary();
  }, []);
  // Agent-related state (move above any usage)
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState('all');
  // For Apply button controls
  const [pendingAgent, setPendingAgent] = useState('all');
  const [topAgents, setTopAgents] = useState([]);
  const [topAgentsLoading, setTopAgentsLoading] = useState(true);
  const [topAgentsError, setTopAgentsError] = useState(null);

  // State for average rating
  const [averageRating, setAverageRating] = useState(null);
  const [averageRatingLoading, setAverageRatingLoading] = useState(false);
  const [averageRatingError, setAverageRatingError] = useState(null);

  // Fetch average rating for the current date range
  const fetchAverageRating = useCallback(async (startDate, endDate) => {
    setAverageRatingLoading(true);
    setAverageRatingError(null);
    try {
      const formattedStartDate = startDate.toISOString().split('T')[0];
      const formattedEndDate = endDate.toISOString().split('T')[0];
      const url = `/dashboard/averageRating?startDate=${formattedStartDate}&endDate=${formattedEndDate}`;
      const resp = await axios.get(url);
      setAverageRating(resp.data.averageRating);
    } catch (err) {
      setAverageRatingError('Failed to load average rating');
      setAverageRating(null);
    } finally {
      setAverageRatingLoading(false);
    }
  }, []);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    endDate: new Date()
  });
  // For Apply button controls
  const [pendingDateRange, setPendingDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    endDate: new Date()
  });

  const [quickDateOption, setQuickDateOption] = useState('mtd');
  // For Apply button controls
  const [pendingQuickDateOption, setPendingQuickDateOption] = useState('mtd');

  const [chatHistory, setChatHistory] = useState([]);
  const [chatModalOpen, setChatModalOpen] = useState(false);
  const [selectedChat, setSelectedChat] = useState(null);
  const [ratingModalOpen, setRatingModalOpen] = useState(false);
  const [selectedRatingChat, setSelectedRatingChat] = useState(null);
  const handleOpenRatingModal = (chat) => {
    setSelectedRatingChat(chat);
    setRatingModalOpen(true);
  };

  const handleCloseRatingModal = () => {
    setRatingModalOpen(false);
    setSelectedRatingChat(null);
  };

  // Fetch recent chat history
  const fetchChatHistory = useCallback(async () => {
    try {
      // Ensure axios has the latest auth token before making the request
      const cookieToken = Cookies.getJwtToken();
      const lsToken = localStorage.getItem('jwtToken');

      console.log('🔍 Token check:', {
        cookieToken: cookieToken ? 'present' : 'missing',
        lsToken: lsToken ? 'present' : 'missing',
        currentAxiosAuth: axios.defaults.headers.common['Authorization'] ? 'set' : 'missing'
      });

      // If we have a token but axios doesn't, update it
      const availableToken = cookieToken || lsToken;
      if (availableToken && !axios.defaults.headers.common['Authorization']) {
        console.log('🔧 Updating global axios auth header');
        axios.defaults.headers.common['Authorization'] = `Bearer ${availableToken}`;
      }

      console.log('🔍 Making request with auth header:',
        axios.defaults.headers.common['Authorization'] ? 'set' : 'missing');

      // Rely on global axios configuration for auth header
      const resp = await axios.get('/dashboard/recentChats');
      console.log('✅ Chat history response:', resp.data);

      // Parse chat_data if it's a JSON string
      const parsedChatHistory = resp.data.map(chat => {
        if (chat.chat_data && typeof chat.chat_data === 'string') {
          try {
            chat.chat_data = JSON.parse(chat.chat_data);
          } catch (e) {
            console.error('Error parsing chat_data for chat:', chat.session_id, e);
          }
        }
        return chat;
      });

      setChatHistory(parsedChatHistory);
    } catch (err) {
      console.error('❌ Error fetching chat history:', err);
      if (err.response) {
        console.error('Response status:', err.response.status);
        console.error('Response data:', err.response.data);
      }

      // If global auth failed, try manual token approach as fallback
      try {
        console.log('🔄 Trying manual token approach...');
        const jwtTokenFromLS = localStorage.getItem('jwtToken');
        const jwtTokenFromCookie = Cookies.getJwtToken();

        console.log('Tokens available:', {
          localStorage: jwtTokenFromLS ? 'present' : 'missing',
          cookie: jwtTokenFromCookie ? 'present' : 'missing'
        });

        const token = jwtTokenFromCookie || jwtTokenFromLS;

        if (token) {
          const authHeader = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
          console.log('🔑 Using manual auth header');
          const retryResp = await axios.get('/dashboard/recentChats', {
            headers: { Authorization: authHeader }
          });
          console.log('✅ Manual auth successful:', retryResp.data);

          // Parse chat_data if it's a JSON string
          const parsedChatHistory = retryResp.data.map(chat => {
            if (chat.chat_data && typeof chat.chat_data === 'string') {
              try {
                chat.chat_data = JSON.parse(chat.chat_data);
              } catch (e) {
                console.error('Error parsing chat_data for chat:', chat.session_id, e);
              }
            }
            return chat;
          });

          setChatHistory(parsedChatHistory);

          // Update global axios for future requests
          axios.defaults.headers.common['Authorization'] = authHeader;
          console.log('🔧 Updated global axios auth for future requests');
        } else {
          console.error('❌ No valid token found for manual retry');
        }
      } catch (retryErr) {
        console.error('❌ Manual retry also failed:', retryErr);
      }
    }
  }, []);

  // Update fetchDashboardData to include selectedAgent
  const fetchDashboardData = useCallback(() => {
    const { startDate, endDate } = dateRange;
    fetchDataWithDates(startDate, endDate, selectedAgent);
    fetchChatHistory(); // chat history not filtered by agent
  }, [dateRange, fetchChatHistory, selectedAgent]);

  const handleQuickDateChange = (e) => {
    const option = e.target.value;
    setQuickDateOption(option);

    const today = new Date();

    let start, end;

    switch (option) {
      case 'today':
        // For "Today", use the exact same date for both start and end
        start = new Date(today);
        start.setHours(0, 0, 0, 0);
        end = new Date(today);
        end.setHours(0, 0, 0, 0);
        break;
      case 'yesterday':
        start = new Date(today);
        start.setDate(today.getDate() - 1);
        start.setHours(0, 0, 0, 0);
        end = new Date(today);
        end.setDate(today.getDate() - 1);
        end.setHours(0, 0, 0, 0);
        break;
      case 'last7days':
        end = new Date(today);
        end.setHours(0, 0, 0, 0);
        start = new Date(today);
        start.setDate(today.getDate() - 6);
        start.setHours(0, 0, 0, 0);
        break;
      case 'mtd':
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        start.setHours(0, 0, 0, 0);
        end = new Date(today);
        end.setHours(0, 0, 0, 0);
        break;
      case 'lastmonth':
        start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        start.setHours(0, 0, 0, 0);
        end = new Date(today.getFullYear(), today.getMonth(), 0);
        end.setHours(0, 0, 0, 0);
        break;
      case 'custom':
        return; // Don't change dates for custom option
      default:
        return;
    }

    console.log(`Setting date range for ${option}:`, {
      start: format(start, 'yyyy-MM-dd HH:mm:ss'),
      end: format(end, 'yyyy-MM-dd HH:mm:ss')
    });

    // For non-custom options, just update pendingDateRange
    if (option !== 'custom') {
      setPendingDateRange({ startDate: start, endDate: end });
    } else {
      setPendingDateRange({ startDate: start, endDate: end });
    }
  };

  // New function to fetch data with specific dates and agent
  const fetchDataWithDates = async (startDate, endDate, agentId = 'all') => {
    try {
      setLoading(true);
      const formattedStartDate = startDate.toISOString().split('T')[0];
      const formattedEndDate = endDate.toISOString().split('T')[0];
      // Add agentId to query if not 'all'
      const agentParam = agentId ? `&agentId=${agentId}` : '';
      const url = `/dashboard/getDashboardData?startDate=${formattedStartDate}&endDate=${formattedEndDate}${agentParam}`;
      console.log('DEBUG: Fetching dashboard data with URL:', url);
      const response = await axios.get(url);
      console.log('DEBUG: Raw backend response from /dashboard/getDashboardData:', response.data);
      if (response.data && response.data.metricsOverTime) {
        console.log('DEBUG: metricsOverTime from backend:', response.data.metricsOverTime);
        if (Array.isArray(response.data.metricsOverTime)) {
          response.data.metricsOverTime.forEach((item, idx) => {
            console.log(`DEBUG: metricsOverTime[${idx}]`, item);
          });
        }
      } else {
        console.log('DEBUG: metricsOverTime is missing from backend response');
      }
      setDashboardData(response.data);
      setError(null);
      // Fetch average rating for this range and agent
      fetchAverageRating(startDate, endDate);
    } catch (err) {
      console.error('DEBUG: Error fetching dashboard data:', err);
      setError('Failed to load dashboard data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  // Fetch average rating on initial load and when date range changes
  useEffect(() => {
    fetchAverageRating(dateRange.startDate, dateRange.endDate);
  }, [dateRange, fetchAverageRating]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 4,
      maximumFractionDigits: 6
    }).format(value);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  useEffect(() => {
    fetchChatHistory();
  }, [fetchChatHistory]);

  useEffect(() => {
    document.title = `Usage Dashboard`;
    // Only fetch on initial load
    fetchDataWithDates(dateRange.startDate, dateRange.endDate, selectedAgent);
  }, []);

  // Always refetch dashboard data when selectedAgent or dateRange changes (after Apply)
  useEffect(() => {
    fetchDataWithDates(dateRange.startDate, dateRange.endDate, selectedAgent);
  }, [selectedAgent, dateRange]);
  useEffect(() => {
    const fetchAgentsAndTop = async () => {
      setTopAgentsLoading(true);
      try {
        const today = new Date();
        const startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        const endDate = new Date(today);
        const formattedStartDate = startDate.toISOString().split('T')[0];
        const formattedEndDate = endDate.toISOString().split('T')[0];
        const url = `/dashboard/getDashboardData?startDate=${formattedStartDate}&endDate=${formattedEndDate}`;
        console.log('[DEBUG] Fetching agents/top with URL:', url);
        const response = await axios.get(url);
        console.log('[DEBUG] agentBreakdown from backend:', response.data?.agentBreakdown);
        setAgents([{ agentId: 'all', agentName: 'All Agents' }, ...response.data?.agentBreakdown || []]);
        setTopAgents((response.data?.agentBreakdown || []).sort((a, b) => (b.sessionCount || 0) - (a.sessionCount || 0)).slice(0, 5));
        setTopAgentsError(null);
      } catch (err) {
        setTopAgentsError('Could not load agent data');
        setAgents([{ agentId: 'all', agentName: 'All Agents' }]);
        setTopAgents([]);
      } finally {
        setTopAgentsLoading(false);
      }
    };
    fetchAgentsAndTop();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Typography variant="h4" color="error" align="center">
          {error}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button variant="contained" onClick={fetchDashboardData}>
            Retry
          </Button>
        </Box>
      </Container>
    );
  }



  const handleOpenChatModal = (chat) => {
    setSelectedChat(chat);
    setChatModalOpen(true);
  };
  const handleCloseChatModal = () => {
    setChatModalOpen(false);
    setSelectedChat(null);
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Top Billing Summary Cards */}
      <Box sx={{ mb: 4, mt: 2 }}>
        <Grid container columns={12} spacing={3} justifyContent="center" alignItems="center">
          {/* Current Billing - Chats */}
          <Grid gridColumn="span 4" sx={{ display: 'flex', justifyContent: 'center' }}>
            <Card sx={{ minWidth: 220, height: '100%' }}>
              <CardContent>
                <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
                  Current Billing - Chats
                </Typography>
                {billingSummary.loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 60 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : billingSummary.error ? (
                  <Typography color="error" align="center">{billingSummary.error}</Typography>
                ) : (
                  <Typography variant="h4" align="center" sx={{ fontWeight: 'bold' }}>{formatNumber(billingSummary.chats)}</Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          {/* Current Billing - Messages */}
          <Grid gridColumn="span 4" sx={{ display: 'flex', justifyContent: 'center' }}>
            <Card sx={{ minWidth: 220, height: '100%' }}>
              <CardContent>
                <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
                  Current Billing - Messages
                </Typography>
                {billingSummary.loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 60 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : billingSummary.error ? (
                  <Typography color="error" align="center">{billingSummary.error}</Typography>
                ) : (
                  <Typography variant="h4" align="center" sx={{ fontWeight: 'bold' }}>{formatNumber(billingSummary.messages)}</Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          {/* Storage Used (always global) */}
          <Grid gridColumn="span 4" sx={{ display: 'flex', justifyContent: 'center' }}>
            <Card sx={{ minWidth: 220, height: '100%' }}>
              <CardContent>
                <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
                  Storage Used
                </Typography>
                {billingSummary.loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 60 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : billingSummary.error ? (
                  <Typography color="error" align="center">{billingSummary.error}</Typography>
                ) : (
                  <Typography variant="h4" align="center" sx={{ fontWeight: 'bold' }}>{billingSummary.storage}</Typography>
                )}
                <Typography variant="caption" color="text.secondary" align="center" sx={{ fontStyle: 'italic', display: 'block', mt: 1 }}>
                  This metric is a running total billed monthly.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Agent and Date Controls */}
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 3, gap: 2 }}>
        {/* Agent Picker */}
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Agent</InputLabel>
          <Select
            value={pendingAgent}
            label="Agent"
            onChange={e => setPendingAgent(e.target.value)}
          >
            {agents.map(agent => (
              <MenuItem key={agent.agentId} value={agent.agentId}>
                {agent.agentName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {/* Date Range Picker */}
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>Date Range</InputLabel>
          <Select
            value={pendingQuickDateOption}
            label="Date Range"
            onChange={e => {
              setPendingQuickDateOption(e.target.value);
              // Update pendingDateRange for quick options
              const today = new Date();
              let start, end;
              switch (e.target.value) {
                case 'today':
                  start = new Date(today);
                  start.setHours(0, 0, 0, 0);
                  end = new Date(today);
                  end.setHours(0, 0, 0, 0);
                  break;
                case 'yesterday':
                  start = new Date(today);
                  start.setDate(today.getDate() - 1);
                  start.setHours(0, 0, 0, 0);
                  end = new Date(today);
                  end.setDate(today.getDate() - 1);
                  end.setHours(0, 0, 0, 0);
                  break;
                case 'last7days':
                  end = new Date(today);
                  end.setHours(0, 0, 0, 0);
                  start = new Date(today);
                  start.setDate(today.getDate() - 6);
                  start.setHours(0, 0, 0, 0);
                  break;
                case 'mtd':
                  start = new Date(today.getFullYear(), today.getMonth(), 1);
                  start.setHours(0, 0, 0, 0);
                  end = new Date(today);
                  end.setHours(0, 0, 0, 0);
                  break;
                case 'lastmonth':
                  start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                  start.setHours(0, 0, 0, 0);
                  end = new Date(today.getFullYear(), today.getMonth(), 0);
                  end.setHours(0, 0, 0, 0);
                  break;
                case 'custom':
                  return; // Don't change dates for custom option
                default:
                  return;
              }
              setPendingDateRange({ startDate: start, endDate: end });
            }}
          >
            <MenuItem value="today">Today</MenuItem>
            <MenuItem value="yesterday">Yesterday</MenuItem>
            <MenuItem value="last7days">Last 7 Days</MenuItem>
            <MenuItem value="mtd">Month to Date</MenuItem>
            <MenuItem value="lastmonth">Last Month</MenuItem>
            <MenuItem value="custom">Custom Range</MenuItem>
          </Select>
        </FormControl>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label="Start Date"
            value={pendingDateRange.startDate}
            onChange={date => setPendingDateRange(prev => ({ ...prev, startDate: date }))}
            disabled={pendingQuickDateOption !== 'custom'}
            renderInput={params => <TextField {...params} size="small" sx={{ minWidth: 120 }} />}
          />
          <DatePicker
            label="End Date"
            value={pendingDateRange.endDate}
            onChange={date => setPendingDateRange(prev => ({ ...prev, endDate: date }))}
            disabled={pendingQuickDateOption !== 'custom'}
            renderInput={params => <TextField {...params} size="small" sx={{ minWidth: 120 }} />}
          />
        </LocalizationProvider>
        <Button
          variant="contained"
          onClick={() => {
            setSelectedAgent(pendingAgent);
            setDateRange({ startDate: pendingDateRange.startDate, endDate: pendingDateRange.endDate });
            setQuickDateOption(pendingQuickDateOption);
            fetchDataWithDates(pendingDateRange.startDate, pendingDateRange.endDate, pendingAgent);
          }}
          sx={{ ml: 2 }}
        >
          Apply
        </Button>
      </Box>

      {/* Line Graphs for Chats and Messages */}
      <Grid container spacing={3} direction="column" sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <Card sx={{ height: 350, width: '100%' }}>
            <CardHeader title="Number of Chats" />
            <CardContent sx={{ height: 250 }}>
              {dashboardData && dashboardData.metricsOverTime && dashboardData.metricsOverTime.length > 0 ? (() => {
                const chatDates = dashboardData.metricsOverTime.map(d => typeof d.date === 'string' ? d.date : '');
                const chatData = dashboardData.metricsOverTime.map(d => {
                  const val = Number(d.totalSessions);
                  return (isNaN(val) || val === null || val === undefined) ? 0 : val;
                });
                const allChatsZero = chatData.every(v => v === 0);
                // Debug overlay
                console.log('DEBUG: Chart Dates:', chatDates);
                console.log('DEBUG: Chart Data (Chats):', chatData);
                if (chatDates.length < 1 || chatData.length < 1 || allChatsZero) {
                  return <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <Typography color="text.secondary">No chat data available for this period.</Typography>
                    <pre style={{ fontSize: 12, color: '#888', marginTop: 8 }}>Dates: {JSON.stringify(chatDates)}
                      Chats: {JSON.stringify(chatData)}</pre>
                  </Box>;
                }
                const chartRows = chatDates.map((date, i) => ({ date, chats: chatData[i] }));
                return (
                  <ResponsiveContainer width="100%" height={250}>
                    <ReLineChart data={chartRows} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis allowDecimals={false} domain={[0, Math.max(...chatData, 10) + 10]} />
                      <ReTooltip />
                      <Legend />
                      <Line type="monotone" dataKey="chats" stroke="#1976d2" name="Chats" />
                    </ReLineChart>
                  </ResponsiveContainer>
                );
              })() : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <CircularProgress size={24} />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Card sx={{ height: 350, width: '100%' }}>
            <CardHeader title="Number of Messages" />
            <CardContent sx={{ height: 250 }}>
              {dashboardData && dashboardData.metricsOverTime && dashboardData.metricsOverTime.length > 0 ? (() => {
                const msgDates = dashboardData.metricsOverTime.map(d => typeof d.date === 'string' ? d.date : '');
                const msgData = dashboardData.metricsOverTime.map(d => {
                  const val = Number(d.totalMessages);
                  return (isNaN(val) || val === null || val === undefined) ? 0 : val;
                });
                const allMsgsZero = msgData.every(v => v === 0);
                // Debug overlay
                console.log('DEBUG: Chart Dates:', msgDates);
                console.log('DEBUG: Chart Data (Messages):', msgData);
                if (msgDates.length < 1 || msgData.length < 1 || allMsgsZero) {
                  return <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <Typography color="text.secondary">No message data available for this period.</Typography>
                    <pre style={{ fontSize: 12, color: '#888', marginTop: 8 }}>Dates: {JSON.stringify(msgDates)}
                      Messages: {JSON.stringify(msgData)}</pre>
                  </Box>;
                }
                const chartRows = msgDates.map((date, i) => ({ date, messages: msgData[i] }));
                return (
                  <ResponsiveContainer width="100%" height={250}>
                    <ReLineChart data={chartRows} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis allowDecimals={false} domain={[0, Math.max(...msgData, 10) + 10]} />
                      <ReTooltip />
                      <Legend />
                      <Line type="monotone" dataKey="messages" stroke="#388e3c" name="Messages" />
                    </ReLineChart>
                  </ResponsiveContainer>
                );
              })() : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <CircularProgress size={24} />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Avg. Chat Duration and Average Chat Rating Cards */}
      <Grid container columns={12} spacing={3} justifyContent="center" alignItems="stretch" sx={{ mb: 3 }}>
        <Grid gridColumn="span 6">
          <Card sx={{ mb: 3 }}>
            <CardHeader title="Avg. Chat Duration (min)" />
            <CardContent>
              {dashboardData ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                  <Typography variant="h4" sx={{ fontWeight: 600 }} align="center">
                    {(dashboardData.summary?.avgSessionDuration || 0).toFixed(1)}
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 60 }}>
                  <CircularProgress size={24} />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid gridColumn="span 6">
          <Card sx={{ mb: 3 }}>
            <CardHeader title="Average Chat Rating" />
            <CardContent>
              {averageRatingLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 60 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : averageRatingError ? (
                <Typography color="error" align="center">{averageRatingError}</Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                  <Rating
                    value={averageRating ? Number(averageRating) : 0}
                    precision={0.1}
                    max={5}
                    readOnly
                    size="large"
                  />
                  <Typography variant="h5" sx={{ fontWeight: 600 }} align="center">
                    {averageRating !== null && !isNaN(averageRating) ? Number(averageRating).toFixed(2) : 'N/A'}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ...existing code for Top Agents, Chat History, etc. remains below... */}

      {/* --- Always render the chart, even if dashboardData is null --- */}
      {/*
      <Card sx={{ mb: 3 }}>
        <CardHeader title="Key Metrics Over Time" />
        <CardContent sx={{ height: 400 }}>
          <LineChart
            xAxis={[{ data: dates, label: 'Date' }]}
            series={[
              { data: totalChats, label: 'Total Chats', color: '#1976d2' },
              { data: totalMessages, label: 'Total Messages', color: '#388e3c' },
              { data: avgDurations, label: 'Avg. Chat Duration (min)', color: '#fbc02d' },
              { data: storageUsed, label: 'Storage Used (bytes)', color: '#7b1fa2' },
            ]}
            height={350}
            legend={{ position: 'bottom' }}
          />
        </CardContent>
      </Card>
      */}

      {/* Model Usage Chart - COMMENTED OUT, can restore later if needed */}
      {/*
      <Card sx={{ mb: 3 }}>
        <CardHeader title="Model Usage Breakdown" />
        <CardContent sx={{ height: 400 }}>
          {modelChartData.length > 0 ? (
            <BarChart
              dataset={modelChartData}
              xAxis={[{ scaleType: 'band', dataKey: 'modelName' }]}
              series={[
                { dataKey: 'tokens', label: 'Tokens', valueFormatter: (value) => formatNumber(value) },
              ]}
              height={350}
            />
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <Typography variant="body1" color="text.secondary">
                No model usage data available
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
      */}

      {/* Recent Usage Table */}
      {/**
      <Card sx={{ mb: 3 }}>
        <CardHeader title="Recent Usage" />
        <CardContent>
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }} aria-label="recent usage table">
              <TableHead>
                <TableRow>
                  <TableCell>Date/Time</TableCell>
                  <TableCell>Model</TableCell>
                  <TableCell align="right">Tokens</TableCell>
                  <TableCell align="right">Cost</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dashboardData.recentUsage && dashboardData.recentUsage.length > 0 ? (
                  dashboardData.recentUsage.map((usage, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        {new Date(usage.timestamp).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        {usage.modelId.includes('claude-3-haiku') ? 'Claude 3 Haiku' :
                         usage.modelId.includes('titan-text-express') ? 'Amazon Titan Text Express' :
                         usage.modelId.includes('jamba-instruct') ? 'AI21 Jamba Instruct' :
                         usage.modelId}
                      </TableCell>
                      <TableCell align="right">{formatNumber(usage.totalTokens)}</TableCell>
                      <TableCell align="right">{formatCurrency(usage.cost)}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} align="center">No recent usage data available</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
      **/}

      {/* Average Rating Card */}
      <Card sx={{ mb: 3 }}>
        <CardHeader title="Average Chat Rating" />
        <CardContent>
          {averageRatingLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 60 }}>
              <CircularProgress size={24} />
            </Box>
          ) : averageRatingError ? (
            <Typography color="error" align="center">{averageRatingError}</Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
              <Rating
                value={averageRating ? Number(averageRating) : 0}
                precision={0.1}
                max={5}
                readOnly
                size="large"
              />
              <Typography variant="h5" sx={{ fontWeight: 600 }} align="center">
                {averageRating !== null && !isNaN(averageRating) ? Number(averageRating).toFixed(2) : 'N/A'}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Top 5 Agents Card */}
      <Card sx={{ mb: 3 }}>
        <CardHeader title="Top 5 Agents by Chats" />
        <CardContent>
          {topAgentsLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 60 }}>
              <CircularProgress size={24} />
            </Box>
          ) : topAgentsError ? (
            <Typography color="error" align="center">{topAgentsError}</Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
              {topAgents.length > 0 ? (
                <ul style={{ paddingLeft: 0, margin: 0, listStyle: 'none', width: '100%' }}>
                  {topAgents.map((agent, index) => (
                    <li key={agent.agentId} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
                      <span style={{ fontWeight: index === 0 ? 'bold' : 'normal', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {agent.agentName}
                      </span>
                      <span style={{ fontWeight: 'bold', color: '#1976d2' }}>
                        {formatNumber(agent.sessionCount)}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <Typography variant="body2" color="text.secondary" align="center" sx={{ width: '100%' }}>
                  No agent data available
                </Typography>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Chat History Table */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title="Recent Chat History"
          action={
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={fetchChatHistory}
            >
              Refresh
            </Button>
          }
        />
        <CardContent>
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 400 }} aria-label="chat history table">
              <TableHead>
                <TableRow>
                  <TableCell>Date Created</TableCell>
                  <TableCell>Agent</TableCell>
                  <TableCell>Messages</TableCell>
                  <TableCell>Rating</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {chatHistory && chatHistory.length > 0 ? (
                  chatHistory.map((chat, idx) => {
                    const hasRating = chat.chat_rating !== null && chat.chat_rating !== undefined;
                    const hasComment = hasRating && chat.chat_rating_comments && chat.chat_rating_comments.trim() !== '';
                    return (
                      <TableRow key={idx} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {chat.internal && (
                              <Tooltip title="Test Chats">
                                <ScienceIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                              </Tooltip>
                            )}
                            {formatDateForDisplay(chat.date_created)}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Button variant="text" onClick={() => handleOpenChatModal(chat)}>
                            {chat.agent_name || chat.agent_id}
                          </Button>
                        </TableCell>
                        <TableCell>
                          {chat.chat_data && chat.chat_data.messages ? chat.chat_data.messages.length : 0}
                        </TableCell>
                        <TableCell>
                          {hasRating ? (
                            hasComment ? (
                              <Tooltip title="Click to view comment">
                                <Button
                                  variant="text"
                                  color="primary"
                                  sx={{ minWidth: 0, p: 0, textTransform: 'none' }}
                                  onClick={() => handleOpenRatingModal(chat)}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Rating value={chat.chat_rating} readOnly max={5} size="small" />
                                    <span style={{ marginLeft: 4 }}>({chat.chat_rating})</span>
                                  </Box>
                                </Button>
                              </Tooltip>
                            ) : (
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Rating value={chat.chat_rating} readOnly max={5} size="small" />
                                <span style={{ marginLeft: 4 }}>({chat.chat_rating})</span>
                              </Box>
                            )
                          ) : (
                            <span>N/A</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} align="center">No chat history available</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
      <ChatHistoryModal open={chatModalOpen} onClose={handleCloseChatModal} chat={selectedChat} />
      <ChatRatingModal open={ratingModalOpen} onClose={handleCloseRatingModal} chat={selectedRatingChat} />
    </Container>
  );
};
export default TenantDashboard;